// USB thermal-printer transport. Holds the user's chosen default printer (by
// vendor/product id) and streams raw ESC/POS bytes to its bulk OUT endpoint.
// In the browser this rides on WebUSB; inside the Android app (no WebUSB in
// the WebView) it goes through the native UsbPrinter Capacitor plugin. Used by
// the receipt + KOT print paths, which fall back to browser printing when no
// USB printer is configured/available.

import { isNativeApp, nativePrint } from './nativeUsb';
import { cachedKey } from './idb';

// ---- Minimal WebUSB typings (not in the default DOM lib) ----
interface UsbEndpoint {
  endpointNumber: number;
  direction: 'in' | 'out';
  type: 'bulk' | 'interrupt' | 'isochronous';
}
interface UsbAlternate {
  endpoints: UsbEndpoint[];
}
interface UsbInterface {
  interfaceNumber: number;
  alternate: UsbAlternate;
}
interface UsbConfiguration {
  interfaces: UsbInterface[];
}
export interface UsbDevice {
  productName?: string;
  manufacturerName?: string;
  serialNumber?: string;
  vendorId: number;
  productId: number;
  opened: boolean;
  configuration: UsbConfiguration | null;
  open(): Promise<void>;
  close(): Promise<void>;
  forget?(): Promise<void>;
  selectConfiguration(n: number): Promise<void>;
  claimInterface(n: number): Promise<void>;
  releaseInterface(n: number): Promise<void>;
  transferOut(endpoint: number, data: BufferSource): Promise<unknown>;
}
interface WebUsb {
  getDevices(): Promise<UsbDevice[]>;
  requestDevice(opts: { filters: unknown[] }): Promise<UsbDevice>;
}

const printerCache = cachedKey<SavedPrinter | null>('sconnect.usbPrinter', null);
// Separate slot for the barcode label printer (TSC / TSPL) so receipts and
// labels can go to different physical devices.
const labelPrinterCache = cachedKey<SavedPrinter | null>('sconnect.usbLabelPrinter', null);

export function getUsb(): WebUsb | undefined {
  return (navigator as unknown as { usb?: WebUsb }).usb;
}

export function isUsbAvailable(): boolean {
  return isNativeApp() || !!getUsb();
}

export interface SavedPrinter {
  vendorId: number;
  productId: number;
  label?: string;
  /** Android USB path (native) — distinguishes two printers that report the
   *  same vendor/product id, e.g. a bill and a label printer of one brand. */
  deviceName?: string;
  /** WebUSB equivalent of the above, when the device reports one. */
  serialNumber?: string;
}

// Does this connected device correspond to the saved printer? Serial number
// wins when both sides have one, so two identical models stay apart; otherwise
// fall back to vendor/product id.
function matches(saved: SavedPrinter, d: { vendorId: number; productId: number; serialNumber?: string }): boolean {
  if (saved.serialNumber && d.serialNumber) return saved.serialNumber === d.serialNumber;
  return saved.vendorId === d.vendorId && saved.productId === d.productId;
}

export function getSavedPrinter(): SavedPrinter | null {
  return printerCache.get();
}

export function savePrinter(p: SavedPrinter): void {
  printerCache.set(p);
}

export function clearPrinter(): void {
  printerCache.clear();
}

// True when WebUSB is supported and a default printer has been chosen.
export function usbPrinterConfigured(): boolean {
  return isUsbAvailable() && !!getSavedPrinter();
}

// ---- Label (barcode) printer slot ----

export function getSavedLabelPrinter(): SavedPrinter | null {
  return labelPrinterCache.get();
}

export function saveLabelPrinter(p: SavedPrinter): void {
  labelPrinterCache.set(p);
}

export function clearLabelPrinter(): void {
  labelPrinterCache.clear();
}

export function labelPrinterConfigured(): boolean {
  return isUsbAvailable() && !!getSavedLabelPrinter();
}

// Send raw bytes (TSPL) to the configured label printer.
export async function printLabelBytes(data: Uint8Array): Promise<void> {
  const saved = getSavedLabelPrinter();
  if (!saved) throw new Error('No USB label printer selected');
  await printBytesTo(saved, data);
}

// Locate the configured printer among the authorized devices.
async function resolveDevice(): Promise<UsbDevice> {
  const usb = getUsb();
  if (!usb) throw new Error('WebUSB is not supported in this browser');
  const saved = getSavedPrinter();
  if (!saved) throw new Error('No USB printer selected');
  const device = (await usb.getDevices()).find((d) => matches(saved, d));
  if (!device) throw new Error('The saved USB printer is not connected/authorized');
  return device;
}

// Find the first bulk OUT endpoint to write ESC/POS data to.
function findOutEndpoint(device: UsbDevice): { interfaceNumber: number; endpoint: number } {
  const config = device.configuration;
  if (!config) throw new Error('USB device has no active configuration');
  for (const intf of config.interfaces) {
    const ep = intf.alternate.endpoints.find((e) => e.direction === 'out' && e.type === 'bulk');
    if (ep) return { interfaceNumber: intf.interfaceNumber, endpoint: ep.endpointNumber };
  }
  throw new Error('No bulk OUT endpoint found on the USB printer');
}

// Send raw bytes to the configured USB printer. Throws if anything goes wrong
// (callers fall back to browser printing).
export async function printBytes(data: Uint8Array): Promise<void> {
  if (isNativeApp()) {
    const saved = getSavedPrinter();
    if (!saved) throw new Error('No USB printer selected');
    await printBytesTo(saved, data);
    return;
  }
  await writeToDevice(await resolveDevice(), data);
}

// Send raw bytes to a SPECIFIC saved printer (per-service-point KOT routing),
// not necessarily the default receipt printer.
export async function printBytesTo(printer: SavedPrinter, data: Uint8Array): Promise<void> {
  if (isNativeApp()) {
    // The native plugin asks for USB permission itself when it has lapsed (it
    // resets on unplug) and prints as soon as it is granted, so a single tap is
    // enough even on the first print to a device. A transient failure (the
    // device re-enumerating, a NAKed first packet) is retried once here rather
    // than left for the user to tap again.
    try {
      await nativePrint(printer.vendorId, printer.productId, data, printer.deviceName);
    } catch (e) {
      await delay(500);
      await nativePrint(printer.vendorId, printer.productId, data, printer.deviceName);
    }
    return;
  }
  const usb = getUsb();
  if (!usb) throw new Error('WebUSB is not supported in this browser');
  const device = (await usb.getDevices()).find((d) => matches(printer, d));
  if (!device) throw new Error(`USB printer ${printer.label ?? ''} is not connected/authorized`);
  try {
    await writeToDevice(device, data);
  } catch (e) {
    // Same story on WebUSB: a device left half-open by a previous job makes the
    // first open/claim fail. Retry once instead of needing a second click.
    await delay(400);
    await writeToDevice(device, data);
  }
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function writeToDevice(device: UsbDevice, data: Uint8Array): Promise<void> {
  await device.open();
  try {
    if (device.configuration === null) await device.selectConfiguration(1);
    const { interfaceNumber, endpoint } = findOutEndpoint(device);
    await device.claimInterface(interfaceNumber);
    await device.transferOut(endpoint, data);
    try {
      await device.releaseInterface(interfaceNumber);
    } catch {
      /* releasing can fail harmlessly on some platforms */
    }
  } finally {
    try {
      await device.close();
    } catch {
      /* ignore close errors */
    }
  }
}
